import { Button, ButtonTheme } from '@cian/button-component';
import * as React from 'react';

import { IAgentSchema, IOfferSchema, IUserSchema } from 'shared/types/api-models/offer-card/v1/get-offer-data';

import { Avatar } from '../../common/components/avatar';
import { HOMEOWNER_PROOF_TEXTS } from '../../constants/homeownerProofTexts';
import { CallSourceWishContainer } from '../../containers/CallSourceWish';
import { CalltrackingBadgeContainer } from '../../containers/CalltrackingBadge';
import { IOfferChatsState } from '../../reducers/chat';
import { IRosreestrInfo } from '../../types/offerData';
import { createAgentIdName } from '../../utils/createAgentIdName';
import { getFormattedPhoneNumber } from '../../utils/formatters';
import { getAgentAvatar } from '../../utils/getAgentAvatar';
import { SimpleBotDetector } from '../../utils/simpleBotDetector';
import { GreenTick } from '../Icons';
import { ABCallButton } from '../ab_call_button';
import { PopupOwnOfferContainer } from '../popups/own_offer/container';
import * as styles from './HomeownerBlock.css';
import { trackFirstMessageClick, trackClickShowPhone } from './HomeownerBlock.tracking';

export interface IHomeownerBlockProps {
  abGroup: number;
  agent: IAgentSchema | null;
  offer: IOfferSchema;
  user: IUserSchema | null;
  offerChat: IOfferChatsState;
  isAuthenticated: boolean;
  isOfferOwnedByUser: boolean;
  isAgentAvailable: boolean;
  isDealRentFormEnabled: boolean;
  rosreestrInfo: IRosreestrInfo | null;

  setPhoneCollapse(): void;
  openDealRentForm(): void;
  openChat(type?: 'open_phone' | 'open_message'): void;
}

export interface IHomeownerBlockState {
  isPhonesVisible: boolean;
  isOwnOfferPopupOpen: boolean;
}

export class HomeownerBlock extends React.PureComponent<IHomeownerBlockProps, IHomeownerBlockState> {
  public state = {
    isOwnOfferPopupOpen: false,
    isPhonesVisible: false,
  };

  private botDetector = new SimpleBotDetector();

  public componentDidMount() {
    this.botDetector.init();
  }

  public render() {
    const { agent, offer, offerChat } = this.props;
    const { phones, homeownerProofs } = offer;
    const { isChatsEnabled } = offerChat;
    const { isOwnOfferPopupOpen } = this.state;

    if (!agent) {
      return null;
    }

    const userIdName = createAgentIdName(agent);
    const avatar = getAgentAvatar(agent);

    return (
      <section className={styles['container']} id="author">
        <Avatar url={avatar} className={styles['image']} isRoundness />

        <h2 className={styles['title']}>Собственник</h2>

        <div className={styles['id']}>{userIdName}</div>

        {Array.isArray(homeownerProofs) && homeownerProofs.length > 0 && (
          <ul className={styles['proofs']}>
            {homeownerProofs.map(proof => (
              <li className={styles['proof']} key={proof}>
                <GreenTick />
                {HOMEOWNER_PROOF_TEXTS[proof]}
              </li>
            ))}
          </ul>
        )}

        {(phones || isChatsEnabled) && (
          <div className={styles['buttons']}>
            {phones && !phones.some(phone => !phone.countryCode || !phone.number) && this.state.isPhonesVisible
              ? this.renderPhones()
              : this.renderShowPhoneButton()}

            {isChatsEnabled && (
              <Button theme={ButtonTheme.Ghost} onClick={this.handleStartChatButtonClick}>
                Написать сообщение
              </Button>
            )}
          </div>
        )}

        {this.state.isPhonesVisible && <CalltrackingBadgeContainer />}
        {this.state.isPhonesVisible && (
          <div className={styles['wish']}>
            <CallSourceWishContainer />
          </div>
        )}

        <PopupOwnOfferContainer isOpen={isOwnOfferPopupOpen} onClose={this.handlePopupOwnOfferClose} />
      </section>
    );
  }

  private renderShowPhoneButton = () => {
    const { offer, isDealRentFormEnabled } = this.props;

    return offer.status === 'published' ? (
      <Button onClick={this.handleShowPhoneClick} className={styles['button']}>
        {isDealRentFormEnabled ? 'Назначить просмотр' : <ABCallButton />}
      </Button>
    ) : null;
  };

  private renderPhones = () => {
    const { phones } = this.props.offer;

    if (!phones || phones.some(phone => !phone.countryCode || !phone.number)) {
      return null;
    }

    return <span className={styles['phones']}>{phones.map(getFormattedPhoneNumber).join(', ')}</span>;
  };

  private handleStartChatButtonClick = () => {
    const { isAgentAvailable, offer, agent, isAuthenticated, isOfferOwnedByUser, openChat } = this.props;

    trackFirstMessageClick(offer, agent);

    switch (true) {
      case !isAuthenticated:
        /* istanbul ignore next */
        if (window.waitForLoginPopup) {
          window.waitForLoginPopup('chats', () =>
            Promise.resolve(openChat(isAgentAvailable ? undefined : 'open_message')),
          );
        }

        break;

      case isOfferOwnedByUser:
        this.setState({ isOwnOfferPopupOpen: true });

        break;

      default:
        openChat(isAgentAvailable ? undefined : 'open_message');
    }
  };

  private openChatFromPhones = () => {
    const {
      isAuthenticated,
      isAgentAvailable,
      isOfferOwnedByUser,
      offerChat: { isChatsEnabled },
      openChat,
    } = this.props;

    if (isChatsEnabled && !isAgentAvailable) {
      if (isOfferOwnedByUser) {
        return this.setState({ isOwnOfferPopupOpen: true });
      }

      if (isAuthenticated) {
        openChat('open_phone');

        return;
      }

      window.waitForLoginPopup?.('chats', () => Promise.resolve(openChat('open_phone')));
    }
  };

  private handleShowPhoneClick = () => {
    const { abGroup, user, offer, agent, setPhoneCollapse, isDealRentFormEnabled, openDealRentForm, rosreestrInfo } =
      this.props;

    if (isDealRentFormEnabled) {
      openDealRentForm();

      return;
    }

    trackClickShowPhone({ offer, agent, abGroup, user, isBot: !this.botDetector.isHuman(), rosreestrInfo });

    setPhoneCollapse();

    this.setState({ isPhonesVisible: true });

    this.openChatFromPhones();
  };

  private handlePopupOwnOfferClose = () => this.setState({ isOwnOfferPopupOpen: false });
}
